import { useCallback, useEffect, useState } from 'react';

interface AsyncLoadingState {
	error: undefined;
	result: undefined;
	state: 'loading';
}

interface AsyncSuccessState<T> {
	error: undefined;
	result: T;
	state: 'success';
}

interface AsyncErrorState {
	error: any;
	result: undefined;
	state: 'error';
}

type AsyncState<T> = AsyncLoadingState | AsyncSuccessState<T> | AsyncErrorState;

const useAsync = <T>(asyncFunction: () => Promise<T>): AsyncState<T> => {
	const [state, setState] = useState<AsyncState<T>>({
		state: 'loading',
		result: undefined,
		error: undefined,
	});
	const executeAsyncFunc = useCallback(
		(isCurrent: { current: boolean }) => {
			setState({ state: 'loading', result: undefined, error: undefined });
			asyncFunction()
				.then((result) => {
					if (isCurrent.current) {
						setState({ state: 'success', result: result, error: undefined });
					}
				})
				.catch((err) => {
					if (isCurrent.current) {
						setState({ state: 'error', result: undefined, error: err });
					}
				});
		},
		[asyncFunction],
	);

	useEffect(() => {
		const isCurrent = { current: true };
		executeAsyncFunc(isCurrent);
		return () => {
			isCurrent.current = false;
		};
	}, [executeAsyncFunc]);

	return state;
};

export default useAsync;
